import type { GQL_ID, NestedFilter } from '@@partner:graphql/types'
import type {
  PriceType,
  DateRangeFilter
} from '@@partner:types:app'

import React, { useCallback } from 'react'
import { Tag } from 'rsuite'
import {
  getSearchableProductStatuses,
  prepareProductsFilter
} from '@@partner:helpers/product'

import {
  FilterPanel,
  createFilter,
  FilterConfig
} from '../../../common/filter'

import CategoriesFilter from './CategoriesFilter'
import ProductBrandsFilter from './ProductBrandFilter'
import ProductGroupSelector from '../form/group/ProductGroupSelector'
import MarketingLabelsFilter from './MarketingLabelsFilter'

// ===== ТИПЫ =====

export interface ProductsFiltersType {
  ids: GQL_ID[]
  xid: GQL_ID[]
  ean: GQL_ID[]
  name: string
  price_type: PriceType[] | undefined
  createdAt: DateRangeFilter
  updatedAt: DateRangeFilter
  stockDate: DateRangeFilter
  priceFrom?: string | number
  priceTo?: string | number
  stockFrom?: string | number
  stockTo?: string | number
  statuses: (string | number)[]
  withPhoto: string | undefined
  indexable: string | undefined
  marketingLabelId: GQL_ID[]
  categoryIds: GQL_ID[]
  brandIds: GQL_ID[]
  productGroupIds: GQL_ID[]
}

// API тип (withPhoto и indexable как boolean)
interface ProductsApiFiltersType extends Omit<ProductsFiltersType, 'withPhoto' | 'indexable'> {
  withPhoto: boolean | undefined
  indexable: boolean | undefined
}

export interface ProductsFilterProps {
  onFilterChanged: (filters: NestedFilter) => void
  showBulkUpdate: boolean
  onShowBulkUpdate: (show: boolean) => void
  canBulkUpdate: boolean
  submitGroupExtra?: React.ReactNode
}

// ===== КОНСТАНТЫ =====

const AVAILABLE_PRODUCT_STATUSES = getSearchableProductStatuses()

const PHOTO_OPTIONS = [
  { value: 'true', label: 'С фото' },
  { value: 'false', label: 'Без фото' }
]

const INDEXABLE_OPTIONS = [
  { value: 'true', label: 'Виден покупателям' },
  { value: 'false', label: 'Скрыт от покупателей' }
]

const PRICE_TYPE_OPTIONS = [
  { value: 'weight', label: 'По весу' },
  { value: 'piece', label: 'Поштучно' }
]

const DEFAULT_STATE: ProductsFiltersType = {
  ids: [],
  xid: [],
  ean: [],
  name: '',
  price_type: undefined,
  createdAt: { from: undefined, to: undefined },
  updatedAt: { from: undefined, to: undefined },
  stockDate: { from: undefined, to: undefined },
  stockFrom: undefined,
  stockTo: undefined,
  priceFrom: undefined,
  priceTo: undefined,
  statuses: [],
  withPhoto: undefined,
  indexable: undefined,
  marketingLabelId: [],
  categoryIds: [],
  brandIds: [],
  productGroupIds: []
}

// ===== КОНФИГУРАЦИЯ ФИЛЬТРОВ =====

const FILTERS_CONFIG: FilterConfig[] = [
  // === Основные фильтры ===
  createFilter.multiline('ids', 'ID товаров (по одному на строку)', {
    rows: 2,
    linePattern: /^\d+$/
  }),

  createFilter.multiline('xid', 'Артикул, штрих-код (по одному на строку)', {
    rows: 2
  }),

  createFilter.text('name', 'Поиск по названию', { size: 'wide' }),

  createFilter.multiselect('statuses', AVAILABLE_PRODUCT_STATUSES, 'Статус', {
    searchable: false,
    renderMenuItem: (label, item) => (
      <Tag key={item?.value} color={item?.color as any}>{label}</Tag>
    )
  }),

  // === Классификатор (expandable) ===
  {
    ...createFilter.custom('categoryIds', 'Категории', ({ value, onChange }) => (
      <CategoriesFilter
        value={value || []}
        onChange={onChange}
      />
    ), { size: 'wide' }),
    expandable: true,
    section: 'Классификатор'
  },

  {
    ...createFilter.custom('productGroupIds', 'Группы товаров', ({ value, onChange }) => (
      <ProductGroupSelector
        value={value || []}
        onChange={(v: GQL_ID[]) => onChange(v || [])}
        multiple={true}
        placeholder="Группы товаров"
      />
    )),
    expandable: true,
    section: 'Классификатор'
  },

  {
    ...createFilter.custom('marketingLabelId', 'Маркетинговые метки', ({ value, onChange }) => (
      <MarketingLabelsFilter
        value={value || []}
        onChange={(v: GQL_ID[]) => onChange(v || [])}
      />
    )),
    expandable: true,
    section: 'Классификатор'
  },

  // === Информация по товарам (expandable) ===
  {
    ...createFilter.select('withPhoto', PHOTO_OPTIONS, 'Фото'),
    expandable: true,
    section: 'Информация по товарам'
  },

  {
    ...createFilter.select('indexable', INDEXABLE_OPTIONS, 'Виден покупателям'),
    expandable: true,
    section: 'Информация по товарам'
  },

  {
    ...createFilter.multiselect('price_type', PRICE_TYPE_OPTIONS, 'Тип цены', {
      searchable: false
    }),
    expandable: true,
    section: 'Информация по товарам'
  },

  {
    ...createFilter.custom('brandIds', 'Производитель', ({ value, onChange }) => (
      <ProductBrandsFilter
        value={value || []}
        onChange={(v: GQL_ID[]) => onChange(v || [])}
        multiple={true}
      />
    )),
    expandable: true,
    section: 'Информация по товарам'
  },

  // === Остатки и цены (expandable) ===
  {
    ...createFilter.numberRange('price', 'Цена', {
      placeholderFrom: 'Цена от',
      placeholderTo: 'Цена до'
    }),
    expandable: true,
    section: 'Остатки и цены'
  },

  {
    ...createFilter.numberRange('stock', 'Остаток', {
      placeholderFrom: 'Остаток от',
      placeholderTo: 'Остаток до'
    }),
    expandable: true,
    section: 'Остатки и цены'
  },

  // === Даты (expandable) ===
  {
    ...createFilter.dateRange('createdAt', 'Дата создания'),
    expandable: true,
    section: 'Даты'
  },

  {
    ...createFilter.dateRange('updatedAt', 'Дата обновления'),
    expandable: true,
    section: 'Даты'
  }
]

// ===== КОМПОНЕНТ =====

export default function ProductsFilter ({
  onFilterChanged,
  showBulkUpdate,
  onShowBulkUpdate,
  canBulkUpdate,
  submitGroupExtra
}: ProductsFilterProps) {
  // Парсинг URL параметров
  const parseUrlParams = useCallback((params: Record<string, any>): Partial<ProductsFiltersType> => {
    const parsed = { ...params }
    // Статусы приходят как строки - конвертируем в числа
    if (parsed.statuses) {
      parsed.statuses = (parsed.statuses as string[]).map((s: string) => parseInt(s))
    }
    return parsed
  }, [])

  // Преобразование перед отправкой в API
  const transformBeforeSubmit = useCallback((state: ProductsFiltersType): ProductsApiFiltersType => {
    return {
      ...state,
      withPhoto: state.withPhoto === 'true' ? true : state.withPhoto === 'false' ? false : undefined,
      indexable: state.indexable === 'true' ? true : state.indexable === 'false' ? false : undefined
    }
  }, [])

  // Финальный обработчик для API
  const handleSearch = useCallback((apiState: ProductsApiFiltersType) => {
    onFilterChanged(prepareProductsFilter(apiState as any))
  }, [ onFilterChanged ])

  return (
    <FilterPanel<ProductsFiltersType, ProductsApiFiltersType>
      filters={FILTERS_CONFIG}
      defaultState={DEFAULT_STATE}
      onSearch={handleSearch}
      parseUrlParams={parseUrlParams}
      transformBeforeSubmit={transformBeforeSubmit}
      showBulkUpdate={showBulkUpdate}
      onBulkUpdate={canBulkUpdate ? onShowBulkUpdate : undefined}
      submitGroupExtra={submitGroupExtra}
    />
  )
}
